import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_BASE_URL } from '@core/services/env.service';
import { VARIABLE_RE, extractFormulaVariables } from '../utils/formula-parser.util';

export { VARIABLE_RE } from '../utils/formula-parser.util';

export const CUSTOM_FORMULA_ID = 'custom';

export interface FormulaValidationResult {
  valid: boolean;
  error?: string;
}

export interface FormulaSegment {
  isVariable: boolean;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class FormulaService {
  constructor(private http: HttpClient) {}

  /** Validates the expression against the backend. Single source of truth for formula rules. */
  validateExpression$(expr: string): Observable<FormulaValidationResult> {
    if (!expr.trim()) return of({ valid: false, error: 'Formula is empty' });
    return this.http.post<{ Expression: string; IsValid: boolean; Error?: string }>(
      `${API_BASE_URL}/formulas/validations`,
      { Expression: expr },
    ).pipe(
      map(res => ({ valid: res.IsValid, error: res.Error })),
      catchError(() => of({ valid: false, error: 'Validation service unavailable' })),
    );
  }

  /**
   * Splits a formula into variable and non-variable segments, in order.
   * Used by the formula input to know which parts become pills.
   */
  splitSegments(formula: string): FormulaSegment[] {
    const segments: FormulaSegment[] = [];
    let last = 0;
    const re = new RegExp(VARIABLE_RE.source, 'g');
    let match: RegExpExecArray | null;

    while ((match = re.exec(formula)) !== null) {
      if (match.index > last) segments.push({ isVariable: false, value: formula.slice(last, match.index) });
      segments.push({ isVariable: true, value: match[1] });
      last = match.index + match[0].length;
    }
    if (last < formula.length) segments.push({ isVariable: false, value: formula.slice(last) });
    return segments;
  }

  /** Returns unique variable names found in the formula, in order of first appearance. */
  extractVariables(formula: string): string[] {
    return extractFormulaVariables(formula);
  }
}
